import { useTranslation } from "react-i18next";

import { buildGoogleMapsEmbedUrl } from "eventLocation/googleMapsEmbed";
import {
  EventLocationValue,
  StructuredEventLocation,
  getEventLocationDisplayDetails,
  getLocationRevealHours,
  shouldRevealEventLocation,
} from "utils";
import type { translate } from "utils";

function ReadonlyEventLocationMap({
  mapQuery,
  title,
}: {
  mapQuery: string;
  title: string;
}) {
  const { i18n } = useTranslation();

  return (
    <iframe
      className="h-52 w-full rounded-xl border-0"
      src={buildGoogleMapsEmbedUrl(mapQuery, i18n.language)}
      title={title}
      loading="lazy"
      referrerPolicy="no-referrer-when-downgrade"
    />
  );
}

interface EventLocationSectionProps {
  location?: EventLocationValue;
  locationDetails?: StructuredEventLocation | null;
  address?: string | null;
  city?: string | null;
  locationVisibility?: string | null;
  eventDate?: string | Date | null;
  isPurchased?: boolean;
  t: typeof translate;
}

function EmptyMapIcon() {
  return (
    <img
      src="/img/event-location-map.svg"
      width={34}
      height={34}
      alt=""
      aria-hidden="true"
    />
  );
}

function LockIcon() {
  return (
    <svg
      width="34"
      height="34"
      viewBox="0 0 34 34"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M10 15H24V27H10V15Z"
        fill="#D8B4FE"
        stroke="#D8B4FE"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path
        d="M13 15V11C13 8.79086 14.7909 7 17 7C19.2091 7 21 8.79086 21 11V15"
        stroke="#D8B4FE"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg
      width="28"
      height="28"
      viewBox="0 0 28 28"
      fill="none"
      aria-hidden="true"
    >
      <circle cx="14" cy="14" r="12" stroke="#D8B4FE" strokeWidth="3" />
      <path
        d="M14 7V14L19 18"
        stroke="#D8B4FE"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export const getEventLocationSectionDisplayState = (
  resolvedLocationDetails: { mapQuery: string },
  locationVisibility: string | null | undefined,
  eventDate: string | Date | null | undefined,
  isPurchased: boolean | undefined
): "missing" | "hidden" | "visible" => {
  if (!resolvedLocationDetails.mapQuery) {
    return "missing";
  }

  return shouldRevealEventLocation(locationVisibility, eventDate, isPurchased)
    ? "visible"
    : "hidden";
};

function EventLocationSection({
  location,
  locationDetails,
  address,
  city,
  locationVisibility,
  eventDate,
  isPurchased = false,
  t,
}: EventLocationSectionProps) {
  const resolvedLocationDetails = getEventLocationDisplayDetails(
    location,
    address,
    city,
    locationDetails
  );
  // Render saved location text. Read-only views must not make Places requests.
  const readableLocationTitle = resolvedLocationDetails.title;
  const displayState = getEventLocationSectionDisplayState(
    resolvedLocationDetails,
    locationVisibility,
    eventDate,
    isPurchased
  );
  const revealHours = getLocationRevealHours(locationVisibility);
  const hiddenLocationTitle = city?.trim() || "";
  const containerClassName = isPurchased
    ? "mt-6 mb-4 pb-2"
    : "mt-6 mb-8 pb-8 border-b border-slate-200";
  const headingClassName = isPurchased
    ? "mb-2 text-base text-slate-500"
    : "mb-5 text-base font-semibold text-slate-900";

  if (displayState === "missing") {
    return (
      <div className={containerClassName}>
        <h3 className={headingClassName}>{t("eventLocation")}</h3>
        <p className="mb-1 text-sm font-medium text-amber-600">
          {t("eventLocationNotSpecified")}
        </p>
        <p className="mb-5 text-sm text-slate-500">
          {t("eventLocationNotSpecifiedDescription")}
        </p>
        <div className="flex h-28 items-center justify-center rounded-xl border border-gray-200 bg-white">
          <EmptyMapIcon />
        </div>
      </div>
    );
  }

  if (displayState === "hidden") {
    const isAfterPurchase = locationVisibility === "after_purchase";
    const helperText = isAfterPurchase
      ? t("exactAddressHiddenUntilPurchase")
      : t("exactAddressRevealedBeforeEvent", { hours: revealHours || 24 });
    const lockedText = isAfterPurchase
      ? t("addressAvailableAfterPurchase")
      : t("addressRevealedBeforeEventShort", { hours: revealHours || 24 });

    return (
      <div className={containerClassName}>
        <h3 className={headingClassName}>{t("eventLocation")}</h3>
        {hiddenLocationTitle ? (
          <p className="mb-1 text-sm font-semibold text-slate-900">
            {hiddenLocationTitle}
          </p>
        ) : null}
        <p className="mb-5 text-sm text-purple-500">{helperText}</p>
        <div className="relative flex h-28 flex-col items-center justify-center rounded-xl border border-purple-200 bg-white text-sm text-purple-500">
          {!isAfterPurchase ? (
            <div className="absolute right-4 top-4">
              <ClockIcon />
            </div>
          ) : null}
          <LockIcon />
          <span>{lockedText}</span>
        </div>
      </div>
    );
  }

  if (isPurchased) {
    return (
      <div className={containerClassName}>
        <p className={headingClassName}>{t("address")}:</p>
        {readableLocationTitle ? (
          <p className="mt-1 text-sm text-slate-900">{readableLocationTitle}</p>
        ) : null}
        {resolvedLocationDetails.address ? (
          <p className="text-sm text-slate-900">
            {resolvedLocationDetails.address}
          </p>
        ) : null}
        {resolvedLocationDetails.mapUrl ? (
          <a
            href={resolvedLocationDetails.mapUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700"
          >
            <span>{t("viewOnMap")}</span>
            <span
              aria-hidden="true"
              className="icon-[material-symbols--arrow-outward] h-4 w-4 shrink-0 text-slate-400"
            />
          </a>
        ) : null}
      </div>
    );
  }

  return (
    <div className={containerClassName}>
      <h3 className={headingClassName}>{t("eventLocation")}</h3>
      {readableLocationTitle ? (
        <p className="mb-1 text-sm font-semibold text-slate-900">
          {readableLocationTitle}
        </p>
      ) : null}
      {resolvedLocationDetails.address ? (
        <p className="mb-4 text-sm text-slate-500">
          {resolvedLocationDetails.address}
        </p>
      ) : null}
      <ReadonlyEventLocationMap
        mapQuery={resolvedLocationDetails.mapQuery}
        title={t("eventLocation")}
      />
    </div>
  );
}

export default EventLocationSection;
